import { z } from 'zod'

const companyStatusEnum = z.enum(['active', 'inactive'])
const companyMemberRoleEnum = z.enum(['manager', 'member'])
const companyMemberStatusEnum = z.enum(['active', 'inactive'])
const optionalEmail = z.string().email('Invalid email').optional().or(z.literal(''))
const optionalUrl = z.string().url('Invalid URL').optional().or(z.literal(''))

export const createCompanySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required').optional(),
  color: z
    .string()
    .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Use a hex like #6366f1')
    .or(z.literal(''))
    .optional(),
  status: companyStatusEnum.optional(),
  managerIds: z.array(z.string()).optional(),
  initialMemberIds: z.array(z.string()).optional(),
})

export const updateCompanySchema = z.object({
  name: z.string().min(1).optional(),
  code: z.string().min(1, 'Code is required').optional(),
  color: z
    .string()
    .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Use a hex like #6366f1')
    .or(z.literal(''))
    .optional(),
  status: companyStatusEnum.optional(),
})

export const companySettingsFormSchema = z.object({
  contactName: z.string().optional(),
  contactEmail: optionalEmail,
  contactPhone: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  timezone: z.string().optional(),
  clientInstructions: z.string().optional(),
  // Org-admin-only site credentials. Flat on the form; mapped to/from the
  // nested settings.credentials block. Non-admins never render these fields.
  credentialConsoleLink: optionalUrl,
  credentialPortalUrl: optionalUrl,
  credentialCompanyId: z
    .string()
    .regex(/^\d{4}$/, 'Company ID must be exactly 4 digits')
    .optional()
    .or(z.literal('')),
  credentialUsername: z.string().optional(),
  credentialPassword: z.string().optional(),
  credentialPhoneUsername: z.string().optional(),
  credentialPhonePassword: z.string().optional(),
  credentialPhoneExtensionNumber: z.string().optional(),
  credentialDispenseDetails: z.string().optional(),
  credentialDispenseExtensionNumbers: z.string().optional(),
  credentialDispenseOperators: z
    .array(
      z.object({
        name: z.string().optional(),
        username: z.string().optional(),
        password: z.string().optional(),
        extensionNumber: z.string().optional(),
      })
    )
    .optional(),
  credentialCustomFields: z
    .array(
      z.object({
        label: z.string().min(1, 'Label is required'),
        value: z.string().optional(),
        type: z.string().optional(),
      })
    )
    .optional(),
  credentialGateCode: z.string().optional(),
  credentialAlarmCode: z.string().optional(),
  credentialWifiName: z.string().optional(),
  credentialWifiPassword: z.string().optional(),
  credentialNotes: z.string().optional(),
})

/**
 * Per-company compensation override. The whole block is optional — admins
 * only need to set it when the user works for multiple companies and the
 * org-level compensation isn't enough.
 *
 * The XOR (compensationType ↔ rate field) is enforced by superRefine because
 * react-hook-form pairs much better with a flat shape than a discriminatedUnion
 * for this form (the override is itself an optional sub-block).
 */
const compensationOverrideShape = z.object({
  compensationType: z.enum(['salaried', 'hourly']),
  baseSalary: z.number().min(0).optional(),
  hourlyRate: z.number().min(0).optional(),
})

export const addCompanyMemberSchema = z.object({
  userId: z.string().min(1, 'User is required'),
  status: companyMemberStatusEnum.optional(),
  role: companyMemberRoleEnum.optional(),
  /** When `setOverride` is true, the form will validate `override` and send it on the wire. */
  setOverride: z.boolean().optional(),
  override: compensationOverrideShape.optional(),
})
.superRefine((value, ctx) => {
  // Only validate the override when the toggle is on. The form holds a
  // populated default override object even when the toggle is off, so we
  // can't rely on .optional() alone — it would still run XOR refinement
  // on the partially-filled default and block submit silently.
  if (!value.setOverride) return

  if (!value.override) {
    ctx.addIssue({
      code: 'custom',
      path: ['override'],
      message: 'Compensation override is required when "Set company-specific compensation" is on',
    })
    return
  }

  const { compensationType, hourlyRate, baseSalary } = value.override
  if (compensationType === 'hourly') {
    if (hourlyRate === undefined || Number.isNaN(hourlyRate)) {
      ctx.addIssue({
        code: 'custom',
        path: ['override', 'hourlyRate'],
        message: 'Hourly rate is required',
      })
    }
    if (baseSalary !== undefined && !Number.isNaN(baseSalary)) {
      ctx.addIssue({
        code: 'custom',
        path: ['override', 'baseSalary'],
        message: 'Base salary must be empty for hourly compensation',
      })
    }
  } else {
    if (baseSalary === undefined || Number.isNaN(baseSalary)) {
      ctx.addIssue({
        code: 'custom',
        path: ['override', 'baseSalary'],
        message: 'Base salary is required',
      })
    }
    if (hourlyRate !== undefined && !Number.isNaN(hourlyRate)) {
      ctx.addIssue({
        code: 'custom',
        path: ['override', 'hourlyRate'],
        message: 'Hourly rate must be empty for salaried compensation',
      })
    }
  }
})

export const updateCompanyMemberSchema = z.object({
  status: companyMemberStatusEnum.optional(),
  role: companyMemberRoleEnum.optional(),
  setOverride: z.boolean().optional(),
  override: compensationOverrideShape.optional(),
})
.superRefine((value, ctx) => {
  if (!value.setOverride) return

  if (!value.override) {
    ctx.addIssue({
      code: 'custom',
      path: ['override'],
      message: 'Compensation override is required when company-specific compensation is selected',
    })
    return
  }

  const { compensationType, hourlyRate, baseSalary } = value.override
  if (compensationType === 'hourly') {
    if (hourlyRate === undefined || Number.isNaN(hourlyRate)) {
      ctx.addIssue({
        code: 'custom',
        path: ['override', 'hourlyRate'],
        message: 'Hourly rate is required',
      })
    }
    if (baseSalary !== undefined && !Number.isNaN(baseSalary)) {
      ctx.addIssue({
        code: 'custom',
        path: ['override', 'baseSalary'],
        message: 'Base salary must be empty for hourly compensation',
      })
    }
  } else {
    if (baseSalary === undefined || Number.isNaN(baseSalary)) {
      ctx.addIssue({
        code: 'custom',
        path: ['override', 'baseSalary'],
        message: 'Base salary is required',
      })
    }
    if (hourlyRate !== undefined && !Number.isNaN(hourlyRate)) {
      ctx.addIssue({
        code: 'custom',
        path: ['override', 'hourlyRate'],
        message: 'Hourly rate must be empty for salaried compensation',
      })
    }
  }
})
export type CreateCompanyFormValues = z.infer<typeof createCompanySchema>
export type UpdateCompanyFormValues = z.infer<typeof updateCompanySchema>
export type CompanySettingsFormValues = z.infer<typeof companySettingsFormSchema>
export type AddCompanyMemberFormValues = z.infer<typeof addCompanyMemberSchema>
export type UpdateCompanyMemberFormValues = z.infer<typeof updateCompanyMemberSchema>
